// What the Insights table shows: one row per employee, role or team (or the
// whole org); how many employees and scores the row covers, and its score;
// then one column per capability (or Activity measure) saying where the row
// stands on it.
//
// An employee's cell is their level. A role's, team's or the org's is the share
// of its employees at Capable or better, the same figure the dashboard gives a
// capability, in the same colours. A score is the same share across every
// capability measured.

import { MEDALS, type MedalKey } from '../../../../../../../components/gamification/scorecardData';
import { LEVEL_COLOR, LEVEL_NAME } from '../../learning-lifecycle/CapabilityGapsList';
import type { CapabilityPersonView } from '../../../../../../../api/capability';
import { ACTIVITY_MEASURE_ORDER } from '../../learning-lifecycle/activityMeasures';
import { tierForPercent } from '../../learning-lifecycle/teamActivity';
import {
  capabilityColor,
  capabilityScore,
  LEVEL_ORDER,
  type CapabilityLevel,
  type CapabilityPair
} from '../../learning-lifecycle/teamCapability';

export type RowsBy = 'employee' | 'role' | 'team' | 'org';
/** View By's choices. The org is not one: it is what the table shows with Organization picked in the filter. */
export const VIEW_BY: Array<{ key: Exclude<RowsBy, 'org'>; label: string }> = [
  { key: 'team', label: 'Team' },
  { key: 'role', label: 'Role' },
  { key: 'employee', label: 'Learner' }
];
export const ROWS_LABEL: Record<RowsBy, string> = {
  employee: 'Name',
  role: 'Role',
  team: 'Team',
  org: 'Organization'
};

export interface Person {
  id: string;
  name: string;
  teamId: string;
  teamName: string;
  roleName: string;
}

export interface MatrixCell {
  text: string;
  color: string;
  /** Not a value: N/A, or nothing captured yet. Shown quietly. */
  quiet?: boolean;
  /** What the cell is ordered by, most to least. Left out, it goes last either way. */
  value?: number;
  /** The scores behind a %, for its breakdown. */
  pairs?: CapabilityPair[];
  /** Behavior: the same cell as Dosen has it (Capability) beside what was observed, shown on hover. */
  versus?: { dosen: { text: string; color: string }; observed: { text: string; color: string } };
}
export interface MatrixColumn {
  key: string;
  name: string;
  /** A capability's own team: it is N/A to every other team, and to a role in the team that does not use it. */
  teamId?: string;
  /** Where the capability comes from: a role's own (absent), or a shared Development or Standards one. */
  source?: string;
  /** A shared capability: one column across every team, matched by its id. */
  capabilityId?: string;
}
export interface MatrixRow {
  id: string;
  label: string;
  /** What sits under the label: an employee's role and team, a role's team. */
  sub?: string;
  /** How many employees the row covers. */
  employees: number;
  /** How many of its employees have been assessed (have a level on at least one capability). Left out where that is not counted (Activity). */
  assessed?: number;
  /** The row's score across every column. */
  score: MatrixCell;
  /** One per column. */
  cells: MatrixCell[];
}

/** The capability is not one of this row's. */
export const NOT_APPLICABLE: MatrixCell = { text: 'N/A', color: '#565656', quiet: true };
const nothingYet = (text: string): MatrixCell => ({ text, color: '#868686', quiet: true });
export const NOT_ASSESSED = nothingYet('Not assessed');
export const NOT_OBSERVED = nothingYet('Not observed');

/** Everyone in the teams in view (GET /v3/admin/capability/insights), once per team they are in. */
export const peopleFromApi = (people: CapabilityPersonView[]): Person[] =>
  people
    .map((p) => ({
      id: p.id,
      name: p.name,
      teamId: p.teamId,
      teamName: p.teamName,
      roleName: p.roleName || 'No Role Yet'
    }))
    .sort((a, b) => a.name.localeCompare(b.name) || a.teamName.localeCompare(b.teamName));

/**
 * The capabilities the pairs cover, team by team: a role's own capability
 * belongs to one team (its roles can share it), so two teams never share a
 * column even when the names match. A shared one (Development, Standards) is
 * the same capability in every team it applies to: one column, after the
 * teams' own.
 */
export const capabilityColumns = (
  pairs: CapabilityPair[],
  teams: Array<{ id: string; name: string }>
): MatrixColumn[] => {
  const columns = new Map<string, MatrixColumn>();
  teams.forEach((team) =>
    pairs
      .filter((p) => p.teamId === team.id)
      .forEach((p) => {
        const shared = p.source === 'development' || p.source === 'standards';
        const key = shared ? `shared|${p.capabilityId}` : `${p.teamId}|${p.capabilityName}`;
        if (!columns.has(key)) {
          columns.set(
            key,
            shared
              ? { key, name: p.capabilityName, source: p.source, capabilityId: p.capabilityId }
              : { key, name: p.capabilityName, teamId: p.teamId, source: 'role' }
          );
        }
      })
  );
  const all = Array.from(columns.values());
  return [...all.filter((c) => !c.capabilityId), ...all.filter((c) => c.capabilityId)];
};

interface Group {
  id: string;
  label: string;
  sub?: string;
  personIds: Set<string>;
}

/** The rows for a grouping, before any values. */
export const groupsFor = (by: RowsBy, people: Person[], orgName: string): Group[] => {
  if (by === 'employee') {
    const byTeam = [...people].sort((a, b) => a.teamName.localeCompare(b.teamName) || a.name.localeCompare(b.name));
    return byTeam.map((p) => ({
      id: p.id,
      label: p.name,
      sub: `${p.roleName} · ${p.teamName}`,
      personIds: new Set([p.id])
    }));
  }
  if (by === 'org') {
    return people.length ? [{ id: 'org', label: orgName, personIds: new Set(people.map((p) => p.id)) }] : [];
  }
  const groups = new Map<string, Group>();
  people.forEach((p) => {
    const id = by === 'team' ? p.teamId : `${p.teamId}|${p.roleName}`;
    const group = groups.get(id) ?? {
      id,
      label: by === 'team' ? p.teamName : p.roleName,
      sub: by === 'team' ? undefined : p.teamName,
      personIds: new Set<string>()
    };
    group.personIds.add(p.id);
    groups.set(id, group);
  });
  return Array.from(groups.values()).sort(
    (a, b) => (a.sub ?? a.label).localeCompare(b.sub ?? b.label) || a.label.localeCompare(b.label)
  );
};

/** The share at Capable or better, or that nothing is captured yet. */
const scoreCell = (pairs: CapabilityPair[], none: MatrixCell): MatrixCell => {
  const { percent } = capabilityScore(pairs);
  return percent === null ? none : { text: `${percent}%`, color: capabilityColor(percent), value: percent, pairs };
};

/**
 * Levels on each capability. An employee's cell is their level; a group's is
 * the share of its assessed employees at Capable or better. `none` is what a
 * cell says when the capability is theirs but nothing is captured yet.
 */
export const levelMatrix = (
  by: RowsBy,
  pairs: CapabilityPair[],
  people: Person[],
  columns: MatrixColumn[],
  orgName: string,
  none: MatrixCell = NOT_ASSESSED
): MatrixRow[] =>
  groupsFor(by, people, orgName).map((group) => {
    const own = pairs.filter((p) => group.personIds.has(p.personId));
    return {
      id: group.id,
      label: group.label,
      sub: group.sub,
      employees: group.personIds.size,
      // Employees with a level on at least one capability: a subset of the row's employees.
      assessed: new Set(own.filter((p) => p.level !== null).map((p) => p.personId)).size,
      score: scoreCell(own, none),
      cells: columns.map((column) => {
        const mine = own.filter((p) =>
          column.capabilityId
            ? p.capabilityId === column.capabilityId
            : p.capabilityName === column.name && p.teamId === column.teamId
        );
        if (mine.length === 0) return NOT_APPLICABLE;
        if (by === 'employee') {
          const { level } = mine[0];
          return level
            ? { text: LEVEL_NAME[level], color: LEVEL_COLOR[level], value: LEVEL_ORDER.indexOf(level) }
            : none;
        }
        return scoreCell(mine, none);
      })
    };
  });

// ---- Behavior -------------------------------------------------------------

/**
 * The observed rows with, on every cell that applies, what Dosen has for the
 * same cell (the Capability tab's value), so the two can be read side by side.
 */
export const withDosenLevels = (observed: MatrixRow[], dosen: MatrixRow[]): MatrixRow[] => {
  const byId = new Map(dosen.map((row) => [row.id, row]));
  const both = (seen: MatrixCell, onDosen?: MatrixCell): MatrixCell =>
    !onDosen || seen === NOT_APPLICABLE
      ? seen
      : {
          ...seen,
          versus: {
            dosen: { text: onDosen.text, color: onDosen.color },
            observed: { text: seen.text, color: seen.color }
          }
        };
  return observed.map((row) => {
    const other = byId.get(row.id);
    return { ...row, score: both(row.score, other?.score), cells: row.cells.map((c, i) => both(c, other?.cells[i])) };
  });
};

// ---- Activity -------------------------------------------------------------

/** The Activity measures as the tabs name them (the BE's names are the keys). */
const MEASURE_LABEL: Record<string, string> = {
  'Lesson Completion': 'Lesson Completion',
  'Learning Range': 'Training & Assessments Started',
  'Proactive Learning': 'Questions to Elliott',
  Roleplays: 'Roleplays',
  'Practice Rounds': 'Practice Roleplays'
};
export const ACTIVITY_COLUMNS: MatrixColumn[] = ACTIVITY_MEASURE_ORDER.map((name) => ({
  key: name,
  name: MEASURE_LABEL[name] ?? name
}));

/** An Activity level's colour on these tables: Low red, Moderate brown, High gold, Excellent green, Exceptional dark green. */
const ACTIVITY_COLOR: Record<MedalKey, string> = {
  wood: '#FF6A55',
  bronze: '#B8794A',
  silver: '#EAB308',
  gold: '#22C55E',
  platinum: '#15803D'
};

/**
 * Activity levels on each measure. An employee's cell is their own level; a
 * group's is the level its employees average to. The score is the level across
 * the five measures, each counting equally.
 */
export const activityMatrix = (
  by: RowsBy,
  people: Person[],
  orgName: string,
  /** Each person's position on each measure (0-100), by person id then measure name. */
  activity: Map<string, Record<string, number>>
): MatrixRow[] =>
  groupsFor(by, people, orgName).map((group) => {
    const ids = Array.from(group.personIds);
    const audience = by === 'employee' ? 'learner' : 'admin';
    const tierCell = (percent: number): MatrixCell => {
      const tier = tierForPercent(percent, audience);
      return { text: MEDALS[tier].label, color: ACTIVITY_COLOR[tier], value: percent };
    };
    const means = ACTIVITY_MEASURE_ORDER.map(
      (measure) => ids.reduce((sum, id) => sum + (activity.get(id)?.[measure] ?? 0), 0) / Math.max(1, ids.length)
    );
    return {
      id: group.id,
      label: group.label,
      sub: group.sub,
      employees: ids.length,
      score: tierCell(means.reduce((a, b) => a + b, 0) / means.length),
      cells: means.map(tierCell)
    };
  });
